import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface ReportInfo {
  kontrolltermin_id: string;
  uuid: string; // Add UUID for download functionality
  report_path: string;
  filename: string;
  file_size: number;
  file_type: string;
  generated_at: string;
  generated_by: string;
  generated_by_name: string;
  sent_at?: string;
  sent_by?: string;
  sent_by_name?: string;
  sent_to?: string;
  version?: number;
  status: 'not_generated' | 'generated' | 'sent';
  metadata?: any;
}

export interface Report {
  id: string;
  uuid: string;
  kontrolltermin_id: string;
  report_category?: ReportCategory; // Report category (standard, qr_etiketten, etc.)
  filename: string;
  file_size: number;
  file_type: string;
  version: number;
  created_at: string;
  created_by: string;
  created_by_name: string;
  sent_at?: string;
  sent_by?: string;
  sent_by_name?: string;
  sent_to?: string[];
  status: 'generated' | 'sent';
  // Client information
  client_name: string;
  client_id?: string;
  client?: {
    id: string;
    name: string;
    nummer?: string;
    status?: string;
  };
  // Standort information
  standort_name: string;
  standort_nummer: string;
  standort_id?: string;
  standort?: {
    id: string;
    name: string;
    nummer: string;
    status?: string;
    adresse?: string;
    bericht_email?: string;
  };
  // Techniker information
  techniker_name: string;
  techniker_id?: string;
  techniker?: {
    id: string;
    full_name: string;
    vorname: string;
    nachname: string;
    email?: string;
  };
  // Kontrolltermin information
  einsatznummer: string;
  kontrolltermin?: {
    id: string;
    einsatznummer: string;
    datum_durchfuerung: string;
    status_id: string;
    global_befall_status_id?: string;
    anmerkungen?: string;
  };
}

export interface ReportsListResponse {
  reports: Report[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    total_pages: number;
  };
}

export interface ReportStatus {
  kontrolltermin_id: string;
  has_report: boolean;
  has_been_sent: boolean;
  download_url?: string;
  status: 'not_generated' | 'generated' | 'sent';
}

export interface EmailTemplate {
  subject: string;
  body: string;
  html?: string; // Optional: full HTML email for preview
}

export interface SendHistory {
  sent_at: string;
  sent_by: string;
  sent_by_name: string;
  sent_to: string;
  email_addresses: string[];
}

export type ReportCategory = 
  | 'standard' 
  | 'auswertungstabelle' 
  | 'systemplan' 
  | 'objektstruktur' 
  | 'besuchsbericht' 
  | 'qr_etiketten' 
  | 'trend_analyse'
  | 'indikatoren_liste';

export interface ReportCategoryOption {
  value: ReportCategory;
  label: string;
  description: string;
  icon: string;
  available: boolean;
}

export interface GenerateReportRequest {
  kontrolltermin_id: string;
  report_category?: ReportCategory;
}

export interface SendReportRequest {
  kontrolltermin_id: string;
  email_addresses: string[];
}

export interface GenerateReportResponse {
  success: boolean;
  report_path: string;
  filename: string;
  file_size: number;
  version: number;
  report_category?: ReportCategory; // Report category
  report_uuid?: string; // Report UUID for HTML preview
  generated_at: string;
  generated_by: string;
  generated_by_name?: string;
}

export interface SendReportResponse {
  success: boolean;
  sent_to: string[];
  sent_at: string;
  sent_by: string;
  email_id?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ReportService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Generate report for a kontrolltermin
   */
  generateReport(kontrollterminId: string, reportCategory: ReportCategory = 'standard'): Observable<GenerateReportResponse> {
    return this.http.post<GenerateReportResponse>(`${this.apiUrl}/report/generate`, {
      kontrolltermin_id: kontrollterminId,
      report_category: reportCategory
    });
  }

  /**
   * Generate report for a standort
   */
  generateStandortReport(standortId: string, reportCategory: ReportCategory = 'objektstruktur', extraParams?: {
    date_from?: string;
    date_to?: string;
    report_type?: string;
    chart_images?: { [key: string]: string };
  }): Observable<Blob> {
    const body: any = {
      report_category: reportCategory
    };

    if (extraParams) {
      if (extraParams.date_from) body.date_from = extraParams.date_from;
      if (extraParams.date_to) body.date_to = extraParams.date_to;
      if (extraParams.report_type) body.report_type = extraParams.report_type;
      if (extraParams.chart_images) body.chart_images = extraParams.chart_images;
    }

    return this.http.post(`${this.apiUrl}/report/standort/${standortId}`, body, {
      responseType: 'blob'
    });
  }

  /**
   * Get trend analysis data for a standort (JSON)
   */
  getTrendData(standortId: string, dateFrom: string, dateTo: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/report/standort/${standortId}/trend-data`, {
      params: {
        date_from: dateFrom,
        date_to: dateTo
      }
    });
  }

  /**
   * Get available report categories
   */
  getAvailableReportCategories(): ReportCategoryOption[] {
    return [
      { value: 'standard', label: 'Standard Besucherbericht', description: 'Vollständiger Monitoringbericht', icon: 'description', available: true },
      { value: 'besuchsbericht', label: 'Besuchsbericht mit Auswertungstabelle', description: 'Besuchsbericht mit Auswertungstabelle', icon: 'assignment', available: true },
      { value: 'auswertungstabelle', label: 'Nur Auswertungstabelle', description: 'Statistische Auswertung nach Systemen', icon: 'table_chart', available: true },
      { value: 'objektstruktur', label: 'Objektstruktur', description: 'Hierarchische Objektstruktur', icon: 'account_tree', available: true },
      { value: 'systemplan', label: 'Systempläne', description: 'Übersicht der Systeme nach Räumen', icon: 'map', available: true },
      { value: 'qr_etiketten', label: 'QR System Etiketten', description: 'QR-Code Etiketten zum Drucken', icon: 'qr_code', available: true },
      { value: 'indikatoren_liste', label: 'Indikatoren-Liste', description: 'Liste aller aktiven Raum-Indikatoren', icon: 'list', available: true },
      { value: 'trend_analyse', label: 'Trend-Analyse', description: 'Trend-Analyse über Zeit', icon: 'trending_up', available: true }
    ];
  }

  /**
   * Get report information for a kontrolltermin
   */
  getReportInfo(kontrollterminId: string): Observable<ReportInfo> {
    return this.http.get<ReportInfo>(`${this.apiUrl}/report/${kontrollterminId}`);
  }

  /**
   * Send report with enhanced recipient data
   */
  sendReportWithRecipients(kontrollterminId: string, recipients: any[], emailSubject?: string, emailBody?: string): Observable<SendReportResponse> {
    const payload: any = {
      kontrolltermin_id: kontrollterminId,
      recipients: recipients
    };
    
    // Add optional email subject and body if provided
    if (emailSubject) {
      payload.email_subject = emailSubject;
    }
    if (emailBody) {
      payload.email_body = emailBody;
    }
    
    return this.http.post<SendReportResponse>(`${this.apiUrl}/report/send`, payload);
  }

  /**
   * Send report via email (legacy method)
   */
  sendReport(kontrollterminId: string, emailAddresses: string[]): Observable<SendReportResponse> {
    return this.http.post<SendReportResponse>(`${this.apiUrl}/report/send`, {
      kontrolltermin_id: kontrollterminId,
      email_addresses: emailAddresses
    });
  }

  /**
   * Download report file
   */
  downloadReport(kontrollterminId: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/report/download/${kontrollterminId}`, {
      responseType: 'blob'
    });
  }

  /**
   * Get email template for preview
   */
  getEmailTemplate(kontrollterminId: string): Observable<EmailTemplate> {
    return this.http.get<EmailTemplate>(`${this.apiUrl}/report/${kontrollterminId}/template`);
  }

  /**
   * Get send history for a kontrolltermin
   */
  getSendHistory(kontrollterminId: string): Observable<SendHistory> {
    return this.http.get<SendHistory>(`${this.apiUrl}/report/${kontrollterminId}/history`);
  }

  /**
   * Check report status
   */
  checkReportStatus(kontrollterminId: string): Observable<ReportStatus> {
    return this.http.get<ReportStatus>(`${this.apiUrl}/report/${kontrollterminId}/status`);
  }

  /**
   * Delete report
   */
  deleteReport(kontrollterminId: string): Observable<{success: boolean; message: string}> {
    return this.http.delete<{success: boolean; message: string}>(`${this.apiUrl}/report/${kontrollterminId}`);
  }

  /**
   * Download report and trigger browser download
   */
  downloadReportFile(kontrollterminId: string, filename: string): void {
    this.downloadReport(kontrollterminId).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        console.error('Error downloading report:', error);
      }
    });
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Validate email addresses
   */
  validateEmailAddresses(emails: string[]): string[] {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emails.filter(email => emailRegex.test(email.trim()));
  }

  /**
   * Get all reports with filtering
   */
  getReports(filters?: {
    client_id?: string;
    standort_id?: string;
    kontrolltermin_id?: string;
    date_from?: string;
    date_to?: string;
    status?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Observable<ReportsListResponse> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
    }
    
    const queryString = params.toString();
    const url = queryString ? `${this.apiUrl}/reports?${queryString}` : `${this.apiUrl}/reports`;
    
    return this.http.get<ReportsListResponse>(url);
  }

  /**
   * Download report by UUID (returns Observable)
   */
  downloadReportByUuid(reportUuid: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/report/download/${reportUuid}`, {
      responseType: 'blob'
    });
  }

  /**
   * Download report by UUID
   */
  downloadReportById(reportUuid: string, report?: Report): void {
    // Make authenticated request to get the file
    this.http.get(`${this.apiUrl}/report/download/${reportUuid}`, {
      responseType: 'blob',
      observe: 'response'
    }).subscribe({
      next: (response) => {
        // Create blob URL and trigger download
        const blob = new Blob([response.body!], { type: response.headers.get('content-type') || 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        
        // Generate nice filename if report data is available, otherwise use header or default
        let filename = 'report.pdf';
        if (report) {
          filename = this.generateReportFilename(report);
        } else {
          // Fallback to Content-Disposition header if no report data
          const contentDisposition = response.headers.get('content-disposition');
          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
            if (filenameMatch) {
              filename = filenameMatch[1];
            }
          }
        }
        
        // Create download link and trigger it
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        console.error('Error downloading report:', error);
        // Show user-friendly error message
        alert('Fehler beim Herunterladen des Berichts. Bitte versuchen Sie es erneut.');
      }
    });
  }

  /**
   * Generate a user-friendly filename for a report
   * Format: <Standort-name>-SBK1-Monitoring-<Year>-<Month>[<Category>].pdf
   * Similar to monitoring app logic
   */
  generateReportFilename(report: Report): string {
    // Get standort name or use default
    const standortName = report.standort_name || 'Standort';
    
    // Sanitize standort name for filename (remove invalid characters)
    const sanitizedStandortName = standortName
      .replace(/[^a-zA-Z0-9äöüÄÖÜß\s-]/g, '') // Remove special chars except spaces and hyphens
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .substring(0, 50); // Limit length
    
    // Get month and year from created_at date
    const createdDate = new Date(report.created_at);
    const monthIndex = createdDate.getMonth(); // 0-11
    const year = createdDate.getFullYear(); // Full 4-digit year (e.g., 2025)
    
    // German month names
    const germanMonths = [
      'Januar', 'Februar', 'Maerz', 'April', 'Mai', 'Juni',
      'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
    ];
    
    const month = germanMonths[monthIndex];
    
    // Get category label if available
    let categorySuffix = '';
    if (report.report_category && report.report_category !== 'standard') {
      const categoryLabels: Record<ReportCategory, string> = {
        'standard': 'Standard',
        'auswertungstabelle': 'Auswertungstabelle',
        'systemplan': 'Systemplan',
        'objektstruktur': 'Objektstruktur',
        'besuchsbericht': 'Besuchsbericht',
        'qr_etiketten': 'QR-Etiketten',
        'trend_analyse': 'Trend-Analyse',
        'indikatoren_liste': 'Indikatoren-Liste'
      };
      const categoryLabel = categoryLabels[report.report_category] || report.report_category;
      // Sanitize category label
      const sanitizedCategory = categoryLabel
        .replace(/[^a-zA-Z0-9äöüÄÖÜß-]/g, '-')
        .replace(/-+/g, '-')
        .trim();
      categorySuffix = `-${sanitizedCategory}`;
    }
    
    // Generate filename: <Standort-name>-SBK1-Monitoring-<Year>-<Month>[<Category>].pdf
    const filename = `${sanitizedStandortName}-SBK1-Monitoring-${year}-${month}${categorySuffix}.pdf`;
    
    return filename;
  }

  /**
   * View report HTML by ID or UUID
   */
  viewReportHtmlById(reportIdOrUuid: string): void {
    // The route is at /report/stored/{report_uuid}/html (not under /v5/)
    // serverUrl is already 'http://localhost:8010/proxy/api', so just append the path
    const url = `${environment.serverUrl}/report/stored/${reportIdOrUuid}/html`;
    console.log('🔗 Opening HTML report URL:', url);
    window.open(url, '_blank');
  }
}
